import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { CreditCard, FileText, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // GBP formatter (UK)
  const formatGBP = useMemo(() => {
    return (amount) => {
      const num = Number(amount || 0);
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    };
  }, []);

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/client/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712]">
        <ClientNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="client-dashboard">
      <ClientNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-slate-400">Welcome back to your client portal</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-card-plan">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <TrendingUp className="text-indigo-400" size={20} />
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                dashboard?.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
              }`}>
                {dashboard?.status}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" data-testid="current-plan">{dashboard?.plan}</h3>
            <p className="text-sm text-slate-400">Current Plan</p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-card-billing">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4">
              <Calendar className="text-purple-400" size={20} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" data-testid="billing-date">
              {dashboard?.next_billing_date ? format(new Date(dashboard.next_billing_date), 'MMM dd') : 'N/A'}
            </h3>
            <p className="text-sm text-slate-400">Next Billing</p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-card-requests">
            <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center mb-4">
              <FileText className="text-pink-400" size={20} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" data-testid="requests-count">{dashboard?.requests_count || 0}</h3>
            <p className="text-sm text-slate-400">Active Requests</p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-card-invoices">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4">
              <CreditCard className="text-cyan-400" size={20} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" data-testid="invoices-count">
              {dashboard?.recent_invoices?.length || 0}
            </h3>
            <p className="text-sm text-slate-400">Recent Invoices</p>
          </div>
        </div>

        {dashboard?.recent_invoices && dashboard.recent_invoices.length > 0 && (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="recent-invoices">
            <h2 className="text-xl font-bold font-['Syne'] text-white mb-4">Recent Invoices</h2>
            <div className="space-y-3">
              {dashboard.recent_invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0"
                  data-testid={`invoice-${invoice.id}`}
                >
                  <div>
                    <p className="text-white font-medium">{formatGBP(invoice.amount)}</p>
                    <p className="text-sm text-slate-400">
                      Due: {format(new Date(invoice.due_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    invoice.status === 'paid' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    {invoice.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
